import React, { useMemo, useRef } from 'react';
import { useCollection } from '../../hooks/useCollection';
import { Link } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import './EventCalendar.css';

export default function EventCalendar() {
  const { documents, error } = useCollection('events', null, ['date', 'desc']);

  // Normalize past_event: support boolean true or string "yes"
  const isPast = (doc) => {
    const v = doc?.past_event;
    if (typeof v === 'string') return v.toLowerCase() === 'yes';
    return !!v; // boolean true
  };

  // Split into upcoming & past
  const { upcomingDocs, pastDocs } = useMemo(() => {
    if (!documents) return { upcomingDocs: [], pastDocs: [] };
    const u = [];
    const p = [];
    for (const d of documents) {
      if (isPast(d)) p.push(d);
      else u.push(d);
    }
    // Sort: upcoming ascending; past descending
    u.sort((a, b) => a.date.toDate() - b.date.toDate());
    p.sort((a, b) => b.date.toDate() - a.date.toDate());
    return { upcomingDocs: u, pastDocs: p };
  }, [documents]);

  // Scroll target for "Past Webinars"
  const pastRef = useRef(null);
  const scrollToPast = () => {
    if (pastRef.current) {
      pastRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="eventCalendarWrapper">
      <Navbar />

      <div className="eventCalendarHeader">
        <h1>Neuromodec Webinars</h1>
      </div>

      {/* Two-column layout: 70% content (upcoming + past), 30% sidebar */}
      <div className="eventCalendarLayout">
        {/* Main content */}
        <div className="eventCalendarMain">
          {error && <p className="error">{error}</p>}

          {/* Upcoming section */}
          <section aria-labelledby="upcoming-title" className="eventSection">
            <h2 id="upcoming-title" className="sectionTitle">Upcoming Webinars</h2>
            {upcomingDocs.length === 0 ? (
              <p className="muted">No upcoming webinars yet—check back soon.</p>
            ) : (
              <ul className="eventList">
                {upcomingDocs.map((doc) => (
                  <li key={doc.id} className="eventListItem">
                    <Link to={`/events/${doc.id}`} className="eventCardLink">
                      <div className="eventCardHorizontal">
                        <div className="eventCardImageWrap">
                          <img src={doc.img_url} alt={doc.name} />
                        </div>
                        <div className="eventCardContent">
                          <h3 className="eventTitle">{doc.name}</h3>
                          <p className="eventMeta">
                            {doc.day === ''
                              ? 'Date: TBD'
                              : `${doc.day}, ${doc.month} ${doc.day_digit}, ${doc.year} | ${doc.start_time} - ${doc.end_time} (ET)`}
                          </p>
                          {doc.type && <p className="eventType">{doc.type}</p>}
                          <span className="upcoming-badge">Upcoming</span>
                        </div>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Past section */}
          <section
            id="past-webinars"
            aria-labelledby="past-title"
            className="eventSection"
            ref={pastRef}
          >
            <h2 id="past-title" className="sectionTitle">Past Webinars</h2>
            {pastDocs.length === 0 ? (
              <p className="muted">No past webinars available yet.</p>
            ) : (
              <ul className="eventList">
                {pastDocs.map((doc) => (
                  <li key={doc.id} className="eventListItem">
                    <Link to={`/events/${doc.id}`} className="eventCardLink">
                      <div className="eventCardHorizontal">
                        <div className="eventCardImageWrap">
                          <img src={doc.img_url} alt={doc.name} />
                        </div>
                        <div className="eventCardContent">
                          <h3 className="eventTitle">{doc.name}</h3>
                          <p className="eventMeta">
                            {doc.day === ''
                              ? 'Date: TBD'
                              : `${doc.day}, ${doc.month} ${doc.day_digit}, ${doc.year} | ${doc.start_time} - ${doc.end_time} (ET)`}
                          </p>
                          {/* {doc.type && <p className="eventType">{doc.type}</p>} */}
                          {/* Past-event label logic */}
{isPast(doc) && (
  doc.type === '2024 Webinar Series' ? (
    <span className="completed-badge">Watch</span>
  ) : doc.type === 'Webinar' ? (
    <span className="completed-badge">Webinar Recording available soon</span>
  ) : null
)}
                        </div>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        {/* Sidebar */}
        <aside className="eventCalendarSidebar">
          <div className="sidebarCard">
            <h4>Past Webinars</h4>
            <p>Catch up on previous sessions from our webinar series</p>
            <button type="button" className="btnPrimary" onClick={scrollToPast}>
              Browse past webinars
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
}
